import { useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useGetSuppliersQuery } from "@/redux/services/inventory/supplier.api";

interface PayeeAutocompleteProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  restaurantId: string;
  placeholder?: string;
  className?: string;
}

/**
 * Free-text beneficiary field with supplier suggestions. Typing filters the
 * restaurant's suppliers, but the value is always whatever the user types — so
 * a supplier can be picked from the list OR a non-supplier (employee, landlord,
 * tax office…) can be entered freely.
 */
const PayeeAutocomplete = ({
  id,
  value,
  onChange,
  restaurantId,
  placeholder,
  className,
}: PayeeAutocompleteProps) => {
  const [open, setOpen] = useState(false);

  // Suppliers are few per restaurant; fetch once and filter client-side for
  // instant as-you-type suggestions. Degrades gracefully to free text if the
  // list can't be loaded (e.g. no supplier access).
  const { data } = useGetSuppliersQuery({
    restaurantId,
    query: { limit: 100 },
  });
  const suppliers = data?.results ?? [];

  const query = value.trim().toLowerCase();

  const matches = useMemo(() => {
    const list = query
      ? suppliers.filter((s) => s.name.toLowerCase().includes(query))
      : suppliers;
    return list.slice(0, 8);
  }, [suppliers, query]);

  // Hide the list once the input already exactly equals the only suggestion —
  // no point offering what's already chosen.
  const showList =
    open &&
    matches.length > 0 &&
    !(matches.length === 1 && matches[0].name.toLowerCase() === query);

  return (
    <div className={cn("relative", className)}>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        // Delay so a click on a suggestion registers before the list unmounts.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        placeholder={placeholder}
      />
      {showList && (
        <ul className="absolute z-50 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-white py-1 shadow-lg">
          {matches.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  // preventDefault keeps the input focused so onBlur doesn't
                  // close the list before onChange fires.
                  e.preventDefault();
                  onChange(s.name);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
              >
                <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-card-foreground">{s.name}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  Fournisseur
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PayeeAutocomplete;
